import { NextPage } from "next";
import { useIntl } from "react-intl";
import { PageProps } from "@/pages/_app";
import Layout from "@/components/layout/Layout";
import { SEO } from "@/interfaces/seo";
import { SEOWithoutTemplate } from "@/interfaces/seoWithoutTemplate";
import HeroBlock, {
  HeroBlockProps,
} from "@/components/common/heroBlock/HeroBlock";
import { VehicleType } from "@/components/common/searchBlock/children/vehicleTypeSelection/VehicleTypeSelection";
import { Article, ArticleProps } from "@/components/common/article/Article";
import { CategoryBlock } from "@/components/common/categoryBlock/CategoryBlock";
import { CategoryCardProps } from "@/components/common/categoryBlock/categoryCard/CategoryCard";
import { Section } from "@/components/common/section/Section";
import {
  DescriptiveBlockList,
  DescriptiveBlockListProps,
} from "@/components/common/descriptiveBlockList/DescriptiveBlockList";
import {
  AdvantageBlock,
  AdvantageBlockProps,
} from "@/components/common/advantageBlock/AdvantageBlock";
import { StaticMap } from "@/components/common/staticMap/StaticMap";
import { FAQProps } from "@/utils/formatCategoryPage";
import { FAQ } from "@/components/pages/vehicle/categoryPage/faq/FAQ";

import { Feature, isFeatureEnabled } from "@/utils/isFeatureEnabled";
import ReviewsBlock2 from "@/components/common/reviewsBlockV1/ReviewsBlock";
import ReviewsBlock1, {
  RentACarReview,
} from "@/components/common/reviewsBlock/ReviewsBlock";
import { StructuredReviewProps } from "@/components/common/structuredData/StructuredData.interface";
import styles from "./OneWay.module.scss";
import {
  MovingInformationBlock,
  MovingInformationBlockProps,
} from "./movingInformationBlock/MovingInformationBlock";

const ReviewsBlock = ({
  name,
  agency,
  rentACarReview,
  reviewsConst,
  image,
}: StructuredReviewProps) => {
  if (!isFeatureEnabled(Feature.NEW_REVIEWS_BLOCK)) {
    return (
      <ReviewsBlock1
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  } else {
    return (
      <ReviewsBlock2
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  }
};

export type OneWayContent = {
  heroBlock: HeroBlockProps;
  vehicleType: VehicleType;
  presentation: ArticleProps | undefined;
  descriptiveBlockList: DescriptiveBlockListProps | undefined;
  categoryPresentation: ArticleProps | undefined;
  categoryCards: CategoryCardProps[] | undefined;
  advantageBlock: AdvantageBlockProps | undefined;
  movingInformationBlock?: MovingInformationBlockProps | undefined;
  faq?: FAQProps;
  rentACarReview: RentACarReview | undefined;
  seo: SEO;
  seoWithoutTemplate: SEOWithoutTemplate;
};
type OneWayProps = PageProps &
  OneWayContent & {
    defaultCanonicalRoute: string;
  };

export const OneWay: NextPage<OneWayProps> = ({
  layout,
  reviewsConst,
  heroBlock,
  presentation,
  descriptiveBlockList,
  categoryPresentation,
  categoryCards,
  advantageBlock,
  movingInformationBlock,
  faq,
  rentACarReview,
  vehicleType,
  seo,
  seoWithoutTemplate,
  defaultCanonicalRoute,
}: OneWayProps) => {
  const intl = useIntl();

  return (
    <>
      <Layout
        layout={layout}
        defaultCanonicalRoute={defaultCanonicalRoute}
        pageMeta={seo}
        title={seoWithoutTemplate.title}
        description={seoWithoutTemplate.description}
        breadcrumbs={[
          { title: intl.formatMessage({ id: "home" }), url: "/" },
          {
            title: intl.formatMessage({ id: `oneWay${vehicleType}Breadcrumb` }),
            url: defaultCanonicalRoute,
          },
        ]}
      >
        <HeroBlock
          {...heroBlock}
          searchBlockInitialState={{
            vehicleType: vehicleType,
            isOneWayTrip: true,
          }}
        />
        <Section>
          {presentation && <Article {...presentation} />}
          {descriptiveBlockList && (
            <DescriptiveBlockList {...descriptiveBlockList} />
          )}
          {categoryPresentation && <Article {...categoryPresentation} />}
        </Section>
        <div className={styles.desktopContainer}>
          {categoryCards && <CategoryBlock categoryCards={categoryCards} />}
        </div>
        {advantageBlock && <AdvantageBlock {...advantageBlock} />}
        {movingInformationBlock && (
          <Section>
            <MovingInformationBlock {...movingInformationBlock} />
          </Section>
        )}
        <Section>
          <StaticMap
            mapName={"agences-aller-simple-rent-a-car_828745"}
            forceZoom="8"
          />
        </Section>

        {faq && <FAQ {...faq} />}

        {rentACarReview && (
          <ReviewsBlock
            rentACarReview={rentACarReview}
            reviewsConst={reviewsConst}
          />
        )}
      </Layout>
    </>
  );
};
